// Ultimate Card Game (UCG) - Match Statistics Engine
// Tracks 6 core KPIs for both teams (western & eastern) throughout a match:
// 1. Kills (Units eliminated)
// 2. Accuracy / Hit % (shotsHit / shotsFired)
// 3. Total Damage Dealt (Units & Bases)
// 4. Plasma Harvested (Starting + Mined)
// 5. Cards Played
// 6. Avg. Territorial Control % (time-weighted control share)
// Plus Match Duration (seconds), and derived Gamerank inputs (see
// js/engine/gamerank.js): Plasma Utilization, Damage/Minute and the
// info-only Material Value Destroyed. See docs/GAMERANK_PLAN.md.

export class MatchStats {
  constructor() {
    this.reset();
  }

  reset() {
    this.duration = 0;
    this.teams = {
      western: this._createTeamStats(),
      eastern: this._createTeamStats()
    };
    // Per-unit tracking for the Unit MVP Spotlight (docs/UNIT_MVP_PLAN.md).
    // Keyed by unit id and kept even after the unit dies/is removed from
    // battlefield.units (which has no death archive of its own) - only
    // non-structure units are registered, see Battlefield.spawnRaw().
    this.unitRecords = new Map();
  }

  _createTeamStats() {
    return {
      kills: 0,
      deaths: 0,
      damageDealt: 0,
      shotsFired: 0,
      shotsHit: 0,
      plasmaHarvested: 20, // Start plasma (BALANCE.startPlasma)
      plasmaSpent: 0,
      plasmaEverCapped: false,
      cardsPlayed: 0,
      territoryControlTimeIntegral: 0, // accumulated (controlFraction * dt)
      materialValueDestroyed: 0, // sum of cardData.cost for every enemy unit this team killed
      superweaponsUsed: 0,
      meldsPerformed: 0,
      airKills: 0, // kills of enemy non-structure Air units (daily quest AIR_SUPERIORITY)
      cardsPlayedByRace: {}, // race -> cards deployed (daily quest FACTION_LOYALTY)
      spellsPlayed: 0, // tactical spells cast (SPELL_MASTERY)
      structuresPlayed: 0, // structures/modules deployed (FORTRESS_PROTOCOL)
      salvagePlasmaEarned: 0, // plasma recovered by Salvage Company / Looting Vulture (SCRAP_HARVEST)
      splashKills: 0, // kills by splash/explosive attacks (SPLASH_CARNAGE)
      airSurgeCompleted: false, // deployed 3 air units within 5s (AIR_WING_SURGE)
      deckHeavyCount: 0, // count of cards in deck with cost >= 40 (TITAN_WARFARE)
      deckMaxCost: 0, // max card cost in deck (LOW_PROFILE)
      deckProfileRecorded: false,
      fogCloakKills: 0, // cloaked units killed during dense fog or metamorphosis fog (MIST_HUNTER)
      antiShadeDuelKills: 0, // enemy Anti Shade killed by own Anti Shade across the veil (VEIL_DUELIST)
      heavyUnitHijacks: 0, // heavy units (40+ cost or Melt) overtaken by Parasite Vulture (NEURAL_HIJACK)
      metamorphosisCast: 0, // Experimental Metamorphosis casts (MUTANT_GAMBIT)
      ownBaseDamageTaken: 0, // total damage taken by own base (IMPENETRABLE_BASTION)
      minOwnBaseHp: 1500, // minimum recorded HP of own base during match (PHOENIX_PROTOCOL)
      _airPlayCount: 0,
      _airPlayT1: 0,
      _airPlayT2: 0,
      _airPlayT3: 0
    };
  }

  update(dt) {
    this.duration += dt;
  }

  recordShot(team, isHit) {
    if (!this.teams[team]) return;
    this.teams[team].shotsFired++;
    if (isHit) {
      this.teams[team].shotsHit++;
    }
  }

  recordDamage(attackerTeam, amount) {
    if (!this.teams[attackerTeam] || amount <= 0) return;
    this.teams[attackerTeam].damageDealt += amount;
  }

  recordKill(killerTeam, victimCost = 0, victimIsAir = false, isSplash = false, victimTeam = null) {
    if (this.teams[killerTeam]) {
      this.teams[killerTeam].kills++;
      if (victimIsAir) this.teams[killerTeam].airKills++;
      if (isSplash) this.teams[killerTeam].splashKills = (this.teams[killerTeam].splashKills || 0) + 1;
      this.teams[killerTeam].materialValueDestroyed += victimCost;
    }
    const victim = victimTeam || (killerTeam === 'western' ? 'eastern' : 'western');
    if (this.teams[victim]) {
      this.teams[victim].deaths = (this.teams[victim].deaths || 0) + 1;
    }
  }

  recordPlasmaCapped(team) {
    if (!this.teams[team]) return;
    this.teams[team].plasmaEverCapped = true;
  }

  // --- Unit MVP Spotlight per-unit tracking (docs/UNIT_MVP_PLAN.md) ---
  // Structures are never registered (Battlefield.spawnRaw() skips them) -
  // "beste Einheit" only considers actual (mobile) units.

  registerUnit(id, cost, team, cardData) {
    this.unitRecords.set(id, {
      team,
      cost,
      cardData,
      unitType: cardData?.unitType || cardData?.name || 'Unknown',
      damageDealt: 0,
      healingDone: 0,
      damageTaken: 0,
      materialCredit: 0,
      spawnedAt: this.duration,
      diedAt: null
    });
  }

  recordUnitDamage(id, amount) {
    const r = this.unitRecords.get(id);
    if (r && amount > 0) r.damageDealt += amount;
  }

  recordUnitHeal(id, amount) {
    const r = this.unitRecords.get(id);
    if (r && amount > 0) r.healingDone += amount;
  }

  recordUnitDamageTaken(id, amount) {
    const r = this.unitRecords.get(id);
    if (r && amount > 0) r.damageTaken += amount;
  }

  recordUnitKillShare(id, materialCredit) {
    const r = this.unitRecords.get(id);
    if (r && materialCredit > 0) r.materialCredit += materialCredit;
  }

  // Idempotent - the first call wins (a unit dies exactly once, but some
  // instant-kill paths call this defensively alongside die()/takeDamage()).
  markUnitDeath(id) {
    const r = this.unitRecords.get(id);
    if (r && r.diedAt === null) r.diedAt = this.duration;
  }

  transferUnit(id, newTeam) {
    const r = this.unitRecords.get(id);
    if (r) r.team = newTeam;
  }

  getUnitLifetime(id) {
    const r = this.unitRecords.get(id);
    if (!r) return 0;
    return (r.diedAt !== null ? r.diedAt : this.duration) - r.spawnedAt;
  }

  recordPlasmaHarvested(team, amount) {
    if (!this.teams[team] || amount <= 0) return;
    this.teams[team].plasmaHarvested += amount;
  }

  recordPlasmaSpent(team, amount) {
    if (!this.teams[team] || amount <= 0) return;
    this.teams[team].plasmaSpent += amount;
  }

  recordCardPlayed(team, race = null) {
    if (!this.teams[team]) return;
    this.teams[team].cardsPlayed++;
    if (race) {
      const byRace = this.teams[team].cardsPlayedByRace;
      byRace[race] = (byRace[race] || 0) + 1;
    }
  }

  recordMeld(team) {
    if (!this.teams[team]) return;
    this.teams[team].meldsPerformed++;
  }

  recordSuperweapon(team) {
    if (!this.teams[team]) return;
    this.teams[team].superweaponsUsed++;
  }

  recordSpellPlayed(team) {
    if (!this.teams[team]) return;
    this.teams[team].spellsPlayed = (this.teams[team].spellsPlayed || 0) + 1;
  }

  recordStructurePlayed(team) {
    if (!this.teams[team]) return;
    this.teams[team].structuresPlayed = (this.teams[team].structuresPlayed || 0) + 1;
  }

  recordSalvagePlasma(team, amount) {
    if (!this.teams[team] || amount <= 0) return;
    this.teams[team].salvagePlasmaEarned = (this.teams[team].salvagePlasmaEarned || 0) + amount;
  }

  recordDeckProfile(team, heavyCount = 0, maxCost = 0) {
    if (!this.teams[team]) return;
    this.teams[team].deckHeavyCount = heavyCount;
    this.teams[team].deckMaxCost = maxCost;
    this.teams[team].deckProfileRecorded = true;
  }

  recordAirPlayed(team) {
    if (!this.teams[team]) return;
    const t = this.teams[team];
    t._airPlayCount = (t._airPlayCount || 0) + 1;
    t._airPlayT1 = t._airPlayT2 || 0;
    t._airPlayT2 = t._airPlayT3 || 0;
    t._airPlayT3 = this.duration;
    if (t._airPlayCount >= 3 && (t._airPlayT3 - t._airPlayT1) <= 5.0) {
      t.airSurgeCompleted = true;
    }
  }

  recordFogCloakKill(killerTeam) {
    if (!this.teams[killerTeam]) return;
    this.teams[killerTeam].fogCloakKills = (this.teams[killerTeam].fogCloakKills || 0) + 1;
  }

  recordAntiShadeDuelKill(killerTeam) {
    if (!this.teams[killerTeam]) return;
    this.teams[killerTeam].antiShadeDuelKills = (this.teams[killerTeam].antiShadeDuelKills || 0) + 1;
  }

  recordHeavyUnitHijack(team) {
    if (!this.teams[team]) return;
    this.teams[team].heavyUnitHijacks = (this.teams[team].heavyUnitHijacks || 0) + 1;
  }

  recordMetamorphosisCast(team) {
    if (!this.teams[team]) return;
    this.teams[team].metamorphosisCast = (this.teams[team].metamorphosisCast || 0) + 1;
  }

  recordBaseHit(victimTeam, damageAmount, newBaseHp) {
    if (!this.teams[victimTeam]) return;
    this.teams[victimTeam].ownBaseDamageTaken = (this.teams[victimTeam].ownBaseDamageTaken || 0) + Math.max(0, damageAmount);
    if (typeof newBaseHp === 'number') {
      this.teams[victimTeam].minOwnBaseHp = Math.min(this.teams[victimTeam].minOwnBaseHp ?? 1500, newBaseHp);
    }
  }

  recordTerritory(dividerX, fieldWidth = 1600, dt = 0) {
    if (fieldWidth <= 0) return;
    const westernFraction = Math.max(0, Math.min(1, dividerX / fieldWidth));
    const easternFraction = 1 - westernFraction;

    const step = dt > 0 ? dt : (1 / 60);
    this.teams.western.territoryControlTimeIntegral += westernFraction * step;
    this.teams.eastern.territoryControlTimeIntegral += easternFraction * step;
  }

  getHitRate(team) {
    const t = this.teams[team];
    if (!t || t.shotsFired === 0) return 100;
    return Math.round((t.shotsHit / t.shotsFired) * 100);
  }

  getAvgTerritoryControl(team) {
    const t = this.teams[team];
    if (!t) return 50;
    const totalIntegral = this.teams.western.territoryControlTimeIntegral + this.teams.eastern.territoryControlTimeIntegral;
    if (totalIntegral <= 0) return 50;
    return Math.max(0, Math.min(100, Math.round((t.territoryControlTimeIntegral / totalIntegral) * 100)));
  }

  // Gamerank input: ratio of enemy material value destroyed to plasma spent
  // (Model A trade efficiency).
  getResourceEfficiency(team) {
    const t = this.teams[team];
    if (!t || t.plasmaSpent <= 0) return 0;
    return t.materialValueDestroyed / t.plasmaSpent;
  }

  // Legacy Gamerank / info metric: how much of harvested plasma was spent.
  getPlasmaUtilization(team) {
    const t = this.teams[team];
    if (!t || t.plasmaHarvested <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((t.plasmaSpent / t.plasmaHarvested) * 100)));
  }

  // Gamerank input: raw damageDealt has no time normalization, so matches of
  // very different lengths aren't comparable on that alone.
  // Effective duration is floored at 60s (1.0 minute) to avoid short early-match
  // spike distortions while keeping per-minute scaling accurate.
  getDamagePerMinute(team) {
    const t = this.teams[team];
    if (!t || this.duration <= 0) return 0;
    const effectiveMinutes = Math.max(1.0, this.duration / 60);
    return t.damageDealt / effectiveMinutes;
  }

  getSummary(team) {
    const t = this.teams[team];
    if (!t) return null;
    return {
      kills: t.kills,
      damageDealt: Math.round(t.damageDealt),
      hitRate: this.getHitRate(team),
      shotsFired: t.shotsFired,
      shotsHit: t.shotsHit,
      plasmaHarvested: Math.round(t.plasmaHarvested),
      plasmaSpent: Math.round(t.plasmaSpent),
      plasmaUtilization: this.getPlasmaUtilization(team),
      resourceEfficiency: this.getResourceEfficiency(team),
      damagePerMinute: this.getDamagePerMinute(team),
      materialValueDestroyed: Math.round(t.materialValueDestroyed),
      cardsPlayed: t.cardsPlayed,
      superweaponsUsed: t.superweaponsUsed || 0,
      meldsPerformed: t.meldsPerformed || 0,
      airKills: t.airKills || 0,
      cardsPlayedByRace: t.cardsPlayedByRace || {},
      spellsPlayed: t.spellsPlayed || 0,
      structuresPlayed: t.structuresPlayed || 0,
      salvagePlasmaEarned: t.salvagePlasmaEarned || 0,
      splashKills: t.splashKills || 0,
      airSurgeCompleted: !!t.airSurgeCompleted,
      deckHeavyCount: t.deckHeavyCount || 0,
      deckMaxCost: t.deckMaxCost || 0,
      deckProfileRecorded: !!t.deckProfileRecorded,
      avgTerritoryControl: this.getAvgTerritoryControl(team),
      duration: this.duration,
      fogCloakKills: t.fogCloakKills || 0,
      antiShadeDuelKills: t.antiShadeDuelKills || 0,
      heavyUnitHijacks: t.heavyUnitHijacks || 0,
      metamorphosisCast: t.metamorphosisCast || 0,
      ownBaseDamageTaken: t.ownBaseDamageTaken || 0,
      minOwnBaseHp: typeof t.minOwnBaseHp === 'number' ? t.minOwnBaseHp : 1500,
      deaths: t.deaths || 0,
      plasmaEverCapped: !!t.plasmaEverCapped
    };
  }

  getFormattedDuration() {
    const totalSeconds = Math.floor(this.duration);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  // Online mode: the server runs the only authoritative simulation, so the
  // client's own MatchStats never accumulates anything (server/matchRoom.js
  // sends this back in the MATCH_END message instead). Raw accumulators
  // only, so every derived getter above (getSummary, getHitRate, ...)
  // produces the exact same numbers a rehydrated instance would compute
  // offline - no second formula implementation to keep in sync.
  serialize() {
    return {
      duration: this.duration,
      teams: {
        western: { ...this.teams.western, cardsPlayedByRace: { ...this.teams.western.cardsPlayedByRace } },
        eastern: { ...this.teams.eastern, cardsPlayedByRace: { ...this.teams.eastern.cardsPlayedByRace } }
      },
      unitRecords: Array.from(this.unitRecords.entries())
    };
  }

  static deserialize(data) {
    const stats = new MatchStats();
    if (!data) return stats;
    stats.duration = data.duration || 0;
    if (data.teams?.western) stats.teams.western = { ...stats.teams.western, ...data.teams.western };
    if (data.teams?.eastern) stats.teams.eastern = { ...stats.teams.eastern, ...data.teams.eastern };
    if (Array.isArray(data.unitRecords)) stats.unitRecords = new Map(data.unitRecords);
    return stats;
  }
}
